import { dbAPI } from './db';

// BPA Vault staged runtime enhancements.
// This module deliberately wraps the existing dbAPI boundary instead of
// rewriting App.jsx or the Google Apps Script contract.

const LOGIN_OVERLAY_ID = 'bpa-login-progress-overlay';
const LOGIN_STAGE_EVENT = 'bpa:login-stage';
let loginFlowActive = false;
let loginOverlay = null;
let currentStage = 'validating';

const STAGES = {
  validating: { label: 'Validating secure access', detail: 'Confirming your Vault passcode…', progress: 24 },
  loading: { label: 'Loading your HTML library', detail: 'Preparing locally stored templates and categories…', progress: 52 },
  syncing: { label: 'Checking cloud sync', detail: 'Checking Google Sheet changes without interrupting local access…', progress: 78 },
  ready: { label: 'Vault ready', detail: 'Your template library is ready to use.', progress: 100 }
};

const emitStage = (stage, extra = {}) => {
  currentStage = stage;
  window.dispatchEvent(new CustomEvent(LOGIN_STAGE_EVENT, { detail: { stage, ...extra } }));
};

const removeLoginOverlay = () => {
  if (loginOverlay?.parentNode) loginOverlay.parentNode.removeChild(loginOverlay);
  loginOverlay = null;
};

const renderStage = (stage) => {
  if (!loginOverlay) return;
  const data = STAGES[stage] || STAGES.validating;
  const label = loginOverlay.querySelector('[data-bpa-login-label]');
  const detail = loginOverlay.querySelector('[data-bpa-login-detail]');
  const bar = loginOverlay.querySelector('[data-bpa-login-bar]');
  if (label) label.textContent = data.label;
  if (detail) detail.textContent = data.detail;
  if (bar) bar.style.width = `${data.progress}%`;
};

const showLoginOverlay = () => {
  if (loginOverlay || document.getElementById(LOGIN_OVERLAY_ID)) return;

  const overlay = document.createElement('div');
  overlay.id = LOGIN_OVERLAY_ID;
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');
  overlay.style.cssText = [
    'position:fixed','inset:0','z-index:2147483646','display:flex','align-items:center','justify-content:center',
    'padding:24px','background:rgba(248,250,252,0.92)','backdrop-filter:blur(8px)'
  ].join(';');

  const card = document.createElement('div');
  card.style.cssText = [
    'width:min(460px,100%)','background:#fff','border:1px solid #e2e8f0','border-radius:20px',
    'box-shadow:0 24px 80px rgba(15,23,42,.18)','padding:32px','font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif'
  ].join(';');

  const shield = document.createElement('div');
  shield.innerHTML = '<div style="width:58px;height:58px;margin:0 auto 18px;border-radius:17px;background:#2563eb;color:#fff;display:flex;align-items:center;justify-content:center;font-size:27px;box-shadow:0 12px 28px rgba(37,99,235,.24)">◆</div>';

  const eyebrow = document.createElement('div');
  eyebrow.textContent = 'BPA VAULT';
  eyebrow.style.cssText = 'text-align:center;font-size:10px;font-weight:900;letter-spacing:.18em;color:#2563eb;margin-bottom:8px';

  const label = document.createElement('h2');
  label.dataset.bpaLoginLabel = '1';
  label.style.cssText = 'margin:0;text-align:center;font-size:21px;font-weight:850;color:#0f172a';

  const detail = document.createElement('p');
  detail.dataset.bpaLoginDetail = '1';
  detail.style.cssText = 'margin:9px 0 22px;text-align:center;font-size:13px;line-height:1.55;color:#64748b';

  const track = document.createElement('div');
  track.style.cssText = 'height:7px;background:#e2e8f0;border-radius:999px;overflow:hidden';
  const bar = document.createElement('div');
  bar.dataset.bpaLoginBar = '1';
  bar.style.cssText = 'height:100%;width:0;background:#2563eb;border-radius:999px;transition:width .45s ease';
  track.appendChild(bar);

  const dots = document.createElement('div');
  dots.innerHTML = '<span></span><span></span><span></span>';
  dots.style.cssText = 'display:flex;justify-content:center;gap:6px;margin-top:18px';
  Array.from(dots.children).forEach((dot, index) => {
    dot.style.cssText = `width:6px;height:6px;border-radius:999px;background:#94a3b8;animation:bpaPulse 1.15s ${index * 0.16}s infinite ease-in-out`;
  });

  if (!document.getElementById('bpa-login-progress-style')) {
    const style = document.createElement('style');
    style.id = 'bpa-login-progress-style';
    style.textContent = '@keyframes bpaPulse{0%,100%{opacity:.28;transform:translateY(0)}50%{opacity:1;transform:translateY(-3px)}}';
    document.head.appendChild(style);
  }

  card.append(shield, eyebrow, label, detail, track, dots);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
  loginOverlay = overlay;
  renderStage(currentStage);
};

window.addEventListener(LOGIN_STAGE_EVENT, (event) => {
  if (!loginFlowActive) return;
  showLoginOverlay();
  renderStage(event.detail?.stage || 'validating');
});

// Start the progress experience only for the main Vault unlock form.
document.addEventListener('submit', (event) => {
  const submitButton = event.target?.querySelector?.('button[type="submit"], button:not([type])');
  if (!submitButton || !/unlock vault/i.test(submitButton.textContent || '')) return;
  loginFlowActive = true;
  emitStage('validating');
});

const originalValidatePasscode = dbAPI.validatePasscode.bind(dbAPI);
dbAPI.validatePasscode = async (...args) => {
  const result = await originalValidatePasscode(...args);
  if (loginFlowActive) {
    if (result?.success) {
      emitStage('loading');
    } else {
      loginFlowActive = false;
      removeLoginOverlay();
    }
  }
  return result;
};

const originalSyncWithRemote = dbAPI.syncWithRemote.bind(dbAPI);
dbAPI.syncWithRemote = async (...args) => {
  if (loginFlowActive) emitStage('syncing');

  let result = await originalSyncWithRemote(...args);

  // Phase 3: do not interpret a cloud/auth endpoint problem as a local logout
  // while the browser still has an unexpired Vault credential. Revalidate once
  // against the existing backend contract before allowing App.jsx to lock.
  if (result?.code === 'SESSION_REQUIRED') {
    const passcode = sessionStorage.getItem('BPA_SESSION_PASSCODE');
    const expiry = Number.parseInt(sessionStorage.getItem('BPA_SESSION_EXPIRY') || '0', 10);
    const locallyValid = Boolean(passcode && expiry && Date.now() < expiry);

    if (locallyValid) {
      const validation = await originalValidatePasscode(args[0], passcode);

      if (validation?.success) {
        result = {
          ...result,
          code: 'SYNC_AUTH_UNAVAILABLE',
          message: 'Cloud sync could not be verified right now. Your Vault remains unlocked and local templates are still available.'
        };
      } else if (validation?.code === 'NETWORK_ERROR' || validation?.code === 'BACKEND_NOT_READY' || validation?.code === 'INVALID_RESPONSE') {
        result = {
          ...result,
          code: 'NETWORK_ERROR',
          message: 'Cloud sync is temporarily unavailable. Your Vault remains unlocked and local templates are still available.'
        };
      }
      // INVALID_PASSCODE intentionally remains SESSION_REQUIRED so a genuinely
      // revoked/changed passcode still locks the Vault as before.
    }
  }

  if (loginFlowActive) {
    emitStage('ready', { syncStatus: result?.status || 'unknown' });
    window.setTimeout(() => {
      loginFlowActive = false;
      removeLoginOverlay();
    }, 650);
  }

  return result;
};